import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Form,
  FormGroup,
  Button,
  FormText,
  Collapse,
} from 'reactstrap';
import { Field, reduxForm } from 'redux-form';
import FormItem from '../../../common/forms/FormItem';
import { isRequired, validateEndDate } from '../../../../utils/validators';
import LocationSelect from '../../../common/forms/select/LocationSelect';
import DatePicker from '../../../common/forms/input/DatePicker';
import TextInput from '../../../common/forms/input/TextInput';
import ServiceTypeSelect from '../../../common/forms/select/ServiceTypeSelect';
import Select from '../../../common/forms/select/Select';
import {
  ITINERARY_SEARCH_DIRECTION_OPTIONS,
  ITINERARY_TYPE_SEARCH_OPTIONS,
} from '../../../../config/constants';
import DriverSelect from '../../../common/forms/select/DriverSelect';
import CabinCrewSelect from '../../../common/forms/select/CabinCrewSelect';

const ItinerarySearchForm = ({
  sourceLocationIsRequired,
  destinationLocationIsRequired,
  handleSubmit,
  searchByDateRange,
  onModal,
  searchByDateRangeAndHour,
  extraButtons,
  filterServiceType,
  multipleServiceTypes,
  extraField,
  showDirectionType,
  showItineraryTypeField,
  showNorteChicoField,
  showDriverField,
  showCabinCrewField,
  showActiveOnlyField,
  showOnlyCommercialField,
  isClearable,
  clearOnUnmountLocation,
  extraFiltersOpenByDefault,
  submitButtonText,
  submitButtonIcon,
  submitButtonColor,
  submitButtonOutline,
  submitButtonDisabled,
  change,
  initialValues,
}) => {
  const [extraFiltersOpen, setExtraFiltersOpen] = useState(
    extraFiltersOpenByDefault,
  );
  const activeOnlyInitialized = useRef(false);
  const onlyCommercialInitialized = useRef(false);

  useEffect(() => {
    if (
      showActiveOnlyField &&
      !activeOnlyInitialized.current &&
      (!initialValues || initialValues.activeOnly === undefined)
    ) {
      change('activeOnly', true);
      activeOnlyInitialized.current = true;
    }
  }, [showActiveOnlyField, change, initialValues]);

  useEffect(() => {
    if (
      showOnlyCommercialField &&
      !onlyCommercialInitialized.current &&
      (!initialValues || initialValues.onlyCommercial === undefined)
    ) {
      change('onlyCommercial', true);
      onlyCommercialInitialized.current = true;
    }
  }, [showOnlyCommercialField, change, initialValues]);

  const toggleExtraFilters = () => setExtraFiltersOpen((prev) => !prev);

  const hasExtraFilters =
    showNorteChicoField ||
    showDriverField ||
    showCabinCrewField ||
    showOnlyCommercialField;

  const validateDate = useCallback(
    (value, allValues) => validateEndDate(value, allValues.fromDateString),
    [],
  );

  const departureTimeStringField = (
    <FormGroup row>
      <FormItem label="Hora">
        <Field
          name="departureTimeString"
          component={TextInput}
          type="time"
          placeholder="HH:mm"
        />
      </FormItem>
    </FormGroup>
  );

  let fromDateLabel = 'Fecha';
  let fromDateName = 'departureDayString';

  const toDateStringField = (
    <FormGroup row>
      <FormItem label="Fecha Final" required>
        <Field
          name="toDateString"
          component={DatePicker}
          validate={[isRequired, validateDate]}
        />
      </FormItem>
    </FormGroup>
  );

  let field = departureTimeStringField;

  if (searchByDateRange) {
    field = toDateStringField;
    fromDateLabel = 'Fecha Inicial';
    fromDateName = 'fromDateString';
  }

  if (searchByDateRangeAndHour) {
    field = (
      <>
        {toDateStringField} {departureTimeStringField}
      </>
    );
    fromDateLabel = 'Fecha Inicial';
    fromDateName = 'fromDateString';
  }

  let formProps = { onSubmit: handleSubmit };
  let buttonProps = { type: 'submit' };

  if (onModal) {
    formProps = null;
    buttonProps = { onClick: handleSubmit, type: 'button' };
  }

  const serviceTypeField = filterServiceType ? (
    <FormGroup row>
      <FormItem label="Tipo de Servicio">
        <Field
          name="serviceType"
          component={ServiceTypeSelect}
          showAll
          isMulti={multipleServiceTypes}
          isClearable={isClearable}
        />
      </FormItem>
    </FormGroup>
  ) : null;

  const directionTypeField = showDirectionType ? (
    <FormGroup row>
      <FormItem label="Dirección del Itinerario">
        <Field
          name="directionType"
          component={Select}
          options={ITINERARY_SEARCH_DIRECTION_OPTIONS}
          isClearable={isClearable}
        />
        <FormText>Este filtro omitirá los campos de Origen y Destino</FormText>
      </FormItem>
    </FormGroup>
  ) : null;

  const itineraryTypeField = showItineraryTypeField ? (
    <FormGroup row>
      <FormItem label="Tipo de Itinerario">
        <Field
          name="itineraryType"
          component={Select}
          options={ITINERARY_TYPE_SEARCH_OPTIONS}
        />
      </FormItem>
    </FormGroup>
  ) : null;

  const norteChicoField = showNorteChicoField ? (
    <FormGroup row>
      <FormItem label="Norte Chico">
        <Field name="showNorteChico" component="input" type="checkbox" />
      </FormItem>
    </FormGroup>
  ) : null;

  const driverField = showDriverField ? (
    <FormGroup row>
      <FormItem label="Conductor">
        <Field
          name="driver"
          component={DriverSelect}
          isClearable={isClearable}
        />
      </FormItem>
    </FormGroup>
  ) : null;

  const cabinCrewField = showCabinCrewField ? (
    <FormGroup row>
      <FormItem label="Tripulante de Cabina">
        <Field
          name="cabinCrew"
          component={CabinCrewSelect}
          isClearable={isClearable}
        />
      </FormItem>
    </FormGroup>
  ) : null;

  const activeOnlyField = showActiveOnlyField ? (
    <FormGroup row>
      <FormItem label="Solo activos">
        <Field name="activeOnly" component="input" type="checkbox" />
      </FormItem>
    </FormGroup>
  ) : null;

  const onlyCommercialField = showOnlyCommercialField ? (
    <FormGroup row>
      <FormItem label="Mostrar solo comercial">
        <Field name="onlyCommercial" component="input" type="checkbox" />
      </FormItem>
    </FormGroup>
  ) : null;

  return (
    <Form {...formProps}>
      {directionTypeField}

      <FormGroup row>
        <FormItem label="Origen" required={sourceLocationIsRequired}>
          <Field
            name="sourceLocationId"
            component={LocationSelect}
            forSales
            validate={sourceLocationIsRequired ? [isRequired] : null}
            isClearable={isClearable}
            clearOnUnmount={clearOnUnmountLocation}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Destino" required={destinationLocationIsRequired}>
          <Field
            name="destinationLocationId"
            component={LocationSelect}
            forSales
            validate={destinationLocationIsRequired ? [isRequired] : null}
            isClearable={isClearable}
            clearOnUnmount={clearOnUnmountLocation}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label={fromDateLabel} required>
          <Field
            name={fromDateName}
            component={DatePicker}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      {field}
      {serviceTypeField}
      {itineraryTypeField}
      {activeOnlyField}
      {extraField}

      {hasExtraFilters && extraFiltersOpenByDefault && (
        <>
          {driverField}
          {cabinCrewField}
          {norteChicoField}
          {onlyCommercialField}
        </>
      )}

      {hasExtraFilters && !extraFiltersOpenByDefault && (
        <>
          <Row className="mb-2">
            <Col>
              <Button
                color="link"
                size="sm"
                onClick={toggleExtraFilters}
                type="button"
                className="p-0"
              >
                <i
                  className={`fa fa-chevron-${
                    extraFiltersOpen ? 'up' : 'down'
                  } mr-1`}
                />
                {extraFiltersOpen
                  ? 'Ocultar filtros adicionales'
                  : 'Mostrar filtros adicionales'}
              </Button>
            </Col>
          </Row>

          <Collapse isOpen={extraFiltersOpen}>
            {driverField}
            {cabinCrewField}
            {norteChicoField}
            {onlyCommercialField}
          </Collapse>
        </>
      )}

      <Row>
        <Col className="flex row-reverse">
          <Button
            color={submitButtonColor}
            outline={submitButtonOutline}
            disabled={submitButtonDisabled}
            {...buttonProps}
          >
            <i className={submitButtonIcon} /> {submitButtonText}
          </Button>
          {extraButtons}
        </Col>
      </Row>
    </Form>
  );
};

ItinerarySearchForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  sourceLocationIsRequired: PropTypes.bool,
  destinationLocationIsRequired: PropTypes.bool,
  searchByDateRange: PropTypes.bool,
  searchByDateRangeAndHour: PropTypes.bool,
  initialValues: PropTypes.shape({
    fromDateString: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.string,
    ]),
    departureDayString: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.string,
    ]),
    activeOnly: PropTypes.bool,
    onlyCommercial: PropTypes.bool,
  }),
  onModal: PropTypes.bool,
  extraButtons: PropTypes.node,
  filterServiceType: PropTypes.bool,
  multipleServiceTypes: PropTypes.bool,
  extraField: PropTypes.node,
  showDirectionType: PropTypes.bool,
  showItineraryTypeField: PropTypes.bool,
  showNorteChicoField: PropTypes.bool,
  showDriverField: PropTypes.bool,
  showCabinCrewField: PropTypes.bool,
  showActiveOnlyField: PropTypes.bool,
  showOnlyCommercialField: PropTypes.bool,
  isClearable: PropTypes.bool,
  clearOnUnmountLocation: PropTypes.bool,
  extraFiltersOpenByDefault: PropTypes.bool,
  submitButtonText: PropTypes.string,
  submitButtonIcon: PropTypes.string,
  submitButtonColor: PropTypes.string,
  submitButtonOutline: PropTypes.bool,
  submitButtonDisabled: PropTypes.bool,
};

ItinerarySearchForm.defaultProps = {
  sourceLocationIsRequired: true,
  destinationLocationIsRequired: true,
  searchByDateRange: false,
  searchByDateRangeAndHour: false,
  initialValues: {
    departureDayString: null,
  },
  onModal: false,
  extraButtons: null,
  showDriverField: false,
  showCabinCrewField: false,
  showActiveOnlyField: false,
  showOnlyCommercialField: false,
  filterServiceType: false,
  multipleServiceTypes: false,
  extraField: null,
  showDirectionType: false,
  showItineraryTypeField: false,
  showNorteChicoField: false,
  isClearable: false,
  clearOnUnmountLocation: true,
  extraFiltersOpenByDefault: false,
  submitButtonText: 'Buscar',
  submitButtonIcon: 'fa fa-search',
  submitButtonColor: 'secondary',
  submitButtonOutline: true,
  submitButtonDisabled: false,
};

const form = reduxForm({
  form: 'ItinerarySearchForm',
})(ItinerarySearchForm);

export default form;
